import {
  ApiError,
  ParsedClientTag,
  Provider,
  ProviderListingResponse,
  ProviderListingResult,
  ProviderListingSearchParams,
} from '@hellodarwin/core/lib/features/entities';
import { DEFAULT_PAGE_SIZE } from '@hellodarwin/core/lib/features/providers/pagination-provider';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi from '../client-api';
import ClientApiV2 from '../client-api-v2';

export interface TagOption {
  value: string;
  label: string;
  category?: string;
}

export interface GroupedTagOption {
  label: string; // Category name (group header)
  options: TagOption[];
}

interface ProviderListingState {
  results: ProviderListingResult[];
  total: number;
  limit: number;
  offset: number;
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: ApiError | null;
  tags: { [key: string]: ParsedClientTag };
  tagsStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  // Single provider state
  selectedProvider: Provider | null;
  selectedProviderStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
}

const initialState: ProviderListingState = {
  results: [],
  total: 0,
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  status: 'idle',
  error: null,
  tags: {},
  tagsStatus: 'idle',
  selectedProvider: null,
  selectedProviderStatus: 'idle',
};

// Fetch available tags for filter dropdowns
export const fetchProviderFilterTags = createAsyncThunk<
  { [key: string]: ParsedClientTag },
  { api: ClientApi },
  { rejectValue: ApiError }
>('client/fetchProviderFilterTags', async ({ api }, { rejectWithValue }) => {
  try {
    return await api.fetchTags();
  } catch (err: any) {
    console.error(err.response?.data);
    return rejectWithValue(err.response?.data);
  }
});

export const fetchProviderById = createAsyncThunk<
  Provider,
  { api: ClientApiV2; providerId: string; locale?: string },
  { rejectValue: ApiError }
>(
  'client/fetchProviderById',
  async ({ api, providerId, locale = 'en' }, { rejectWithValue }) => {
    try {
      const response = await api.get<Provider>(
        `/providers/${providerId}?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response?.data);
      showErrorNotification(err.response?.data);
      return rejectWithValue(err.response?.data);
    }
  },
);

export const queryProviderListing = createAsyncThunk<
  ProviderListingResponse,
  { api: ClientApiV2; params: ProviderListingSearchParams },
  { rejectValue: ApiError }
>(
  'client/queryProviderListing',
  async ({ api, params }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.hourly_rate_min)
        queryParams.append(
          'hourly_rate_min',
          params.hourly_rate_min.toString(),
        );
      if (params.hourly_rate_max)
        queryParams.append(
          'hourly_rate_max',
          params.hourly_rate_max.toString(),
        );

      // Array parameters (comma-separated)
      if (params.tags && params.tags.length > 0) {
        queryParams.append('tags', params.tags.join(','));
      }
      if (params.service_tags && params.service_tags.length > 0) {
        queryParams.append('service_tags', params.service_tags.join(','));
      }
      if (params.specialty_tags && params.specialty_tags.length > 0) {
        queryParams.append('specialty_tags', params.specialty_tags.join(','));
      }
      if (params.provider_type && params.provider_type.length > 0) {
        queryParams.append('provider_type', params.provider_type.join(','));
      }
      if (params.business_size && params.business_size.length > 0) {
        queryParams.append('business_size', params.business_size.join(','));
      }
      if (params.languages && params.languages.length > 0) {
        queryParams.append('languages', params.languages.join(','));
      }

      const response = await api.get<ProviderListingResponse>(
        `/providers?${queryParams.toString()}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response?.data);
      showErrorNotification(err.response?.data);
      return rejectWithValue(err.response?.data);
    }
  },
);

const providerListingSlice = createSlice({
  name: 'providerListing',
  initialState,
  reducers: {
    resetProviderListing: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(queryProviderListing.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(queryProviderListing.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.results = action.payload.results;
        state.total = action.payload.total;
        state.limit = action.payload.limit;
        state.offset = action.payload.offset;
      })
      .addCase(queryProviderListing.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || null;
      })
      // Tags fetching
      .addCase(fetchProviderFilterTags.pending, (state) => {
        state.tagsStatus = 'pending';
      })
      .addCase(fetchProviderFilterTags.fulfilled, (state, action) => {
        state.tagsStatus = 'succeeded';
        state.tags = action.payload;
      })
      .addCase(fetchProviderFilterTags.rejected, (state) => {
        state.tagsStatus = 'failed';
      })
      // Single provider fetching
      .addCase(fetchProviderById.pending, (state) => {
        state.selectedProviderStatus = 'pending';
      })
      .addCase(fetchProviderById.fulfilled, (state, action) => {
        state.selectedProviderStatus = 'succeeded';
        state.selectedProvider = action.payload;
      })
      .addCase(fetchProviderById.rejected, (state) => {
        state.selectedProviderStatus = 'failed';
        state.selectedProvider = null;
      });
  },
});

export const { resetProviderListing } = providerListingSlice.actions;

// Selectors
const selectProviderListingState = (state: RootState) => state.providerListing;

export const selectProviderListingResults = createSelector(
  [selectProviderListingState],
  (state: ProviderListingState) => state.results,
);

export const selectProviderListingTotal = createSelector(
  [selectProviderListingState],
  (state: ProviderListingState) => state.total,
);

export const selectProviderListingStatus = createSelector(
  [selectProviderListingState],
  (state) => state.status,
);

export const selectProviderListingIsLoading = createSelector(
  [selectProviderListingState],
  (state) => state.status === 'pending',
);

export const selectProviderListingPagination = createSelector(
  [selectProviderListingState],
  (state) => ({
    total: state.total,
    limit: state.limit,
    offset: state.offset,
  }),
);

// Tag selectors for filter dropdowns
export const selectProviderFilterTags = createSelector(
  [selectProviderListingState],
  (state) => state.tags,
);

export const selectProviderFilterTagsLoading = (state: RootState): boolean => {
  return selectProviderListingState(state).tagsStatus === 'pending';
};

// Category tag structure for filter component
export interface CategoryTagData {
  label: string;
  tag: string;
  services: { label: string; tag: string }[];
  specialties: { label: string; tag: string }[];
}

// Extract the label (last segment) from a full tag path
// e.g., "categories:applications development:services:desktop" -> "desktop"
const extractLabelFromTag = (fullTag: string): string => {
  const parts = fullTag.split(':');
  return parts[parts.length - 1];
};

// Get all categories with their services and specialties
// Note: Backend returns full tag paths for services/specialties, so we use them directly
export const selectCategoriesWithChildren = (
  state: RootState,
): CategoryTagData[] => {
  const tags = selectProviderFilterTags(state) as {
    [key: string]: ParsedClientTag;
  };
  return Object.entries(tags).map(([category, tagData]) => ({
    label: category,
    tag: `categories:${category}`,
    services: (tagData.services || []).map((serviceTag: string) => ({
      label: extractLabelFromTag(serviceTag),
      tag: serviceTag, // Use full path directly from backend
    })),
    specialties: (tagData.specialties || []).map((specialtyTag: string) => ({
      label: extractLabelFromTag(specialtyTag),
      tag: specialtyTag, // Use full path directly from backend
    })),
  }));
};

// Get category options for dropdown (simple list)
export const selectCategoryOptions = (state: RootState): TagOption[] => {
  const tags = selectProviderFilterTags(state);
  return Object.keys(tags).map((category) => ({
    value: `categories:${category}`,
    label: category,
  }));
};

// Single provider selectors
export const selectProviderById = (
  state: RootState,
  _providerId: string,
): Provider | null => {
  return selectProviderListingState(state).selectedProvider;
};

export const selectProviderByIdLoading = (state: RootState): boolean => {
  return selectProviderListingState(state).selectedProviderStatus === 'pending';
};

export default providerListingSlice.reducer;
